import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon, PencilIcon, RefreshCwIcon, ExternalLinkIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { apiClient, TenantResponse, TenantLog, CreateTenantChecklist, SdlOnboardingChecklist, BuildClientContentChecklist } from '../lib/api'
import TenantFeedbackTab from '../components/TenantFeedbackTab'
import SDLAgentTab from '../components/SDLAgentTab'
import TenantDomainExpertsTab from '../components/TenantDomainExpertsTab'
import { getTenantDisplayUrl, getTenantUrl } from '../lib/utils'
import { getTenantStatusBadgeClass, getTenantStatusLabel } from '../lib/tenantStatus'
import EditTenantModal from '../components/EditTenantModal'
import SOTFileBrowser from '../components/SOTFileBrowser'
import Toast from '../components/Toast'

// ── Label maps for checklist fields ─────────────────────────────────────────

const createTenantLabels: Record<keyof CreateTenantChecklist, string> = {
  tenant_created_on_lawmabase: 'Create Tenant on lawmabase',
  dns_entries_added: 'Add dns entries to point to client admin',
  custom_domains_added_in_azure: 'Add custom domains in Azure web app for client admin',
  ssl_certificates_created_and_tested: 'Create and test ssl certificates',
}

const sdlLabels: Record<keyof SdlOnboardingChecklist, string> = {
  crawl_mds_uploaded: (subdomain: string) => `Upload crawl .mds to TenantData/${subdomain}/crawl/md/documents`,
  marketing_audit_uploaded: (subdomain: string) => `Upload MARKETING-AUDIT.md to TenantData/${subdomain}/doc-prep-raw/audit-report/MARKETING-AUDIT.md`,
  competitor_crawl_uploaded: (subdomain: string) => `Upload competitor crawl .json(s) to TenantData/${subdomain}/doc-prep-raw/competitors-crawl`,
  questionaire_uploaded: (subdomain: string) => `Upload ${subdomain}_raw_questionaire_transcript.txt`,
  other_mds_uploaded: (subdomain: string) => `Upload other .mds to TenantData/${subdomain}/other/md/documents`,
  sdl_generated: 'Generate SDL',
  sdl_uploaded_to_qm: 'Uploaded SDL to Question Management',
  complete_sdl_qa_pass: 'Complete SDL QA Pass',
} as any

const buildContentLabels: Record<keyof BuildClientContentChecklist, string> = {
  assets_added: 'Add Assets via Client Admin',
  faq_category_labels_created: 'Create necessary labels for FAQ categories for Questions and test',
  ctas_setup: 'Setup CTAs',
  homepages_and_variations_setup: 'Setup HomePages and HomePage Variations',
  production_key_created: 'Create Production Key',
  preview_key_created: 'Create Preview Key',
  knowledgebase_sync_job_added: 'Add a Knowledgebase sync job and do first sync',
  reviews_sync_job_added: 'Add reviews sync job and do first run',
  integrations_added: 'Add necessary integrations',
  appointment_scheduling_configured: 'Turn on Appointment scheduling if the client wants it',
  widget_configuration_done: 'Widget configuration done',
  contact_settings_adjusted: 'Contact settings adjusted',
  advanced_prompt_customized: 'Advanced prompt customized',
  ama_fully_tested: 'AMA fully tested',
  questions_fully_tested: 'Questions fully tested',
}

function getLabel(labelMap: Record<string, any>, key: string, subdomain: string): string {
  const val = labelMap[key]
  if (typeof val === 'function') return val(subdomain)
  return val || key
}

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ done }: { done: boolean }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${done ? 'bg-green-100 text-green-700' : 'bg-yellow-50 text-yellow-700'
        }`}
    >
      {done ? 'Done' : 'Pending'}
    </span>
  )
}

// ── Checklist accordion section ───────────────────────────────────────────────

interface ChecklistSectionProps {
  title: string
  items: { key: string; label: string; done: boolean }[]
  badgeLabel: string
  badgeColor: string
  completedCount: number
  totalCount: number
}

function ChecklistSection({ title, items, badgeLabel, badgeColor, completedCount, totalCount }: ChecklistSectionProps) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 transition-colors px-1"
      >
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${badgeColor}`}>
            {badgeLabel}
          </span>
          <span className="text-sm font-medium text-gray-600">
            {completedCount}/{totalCount}
          </span>
          {open ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {open && (
        <div className="pb-3 space-y-0">
          {items.map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between py-3 px-1 border-t border-gray-50"
            >
              <span className={`text-sm ${item.done ? 'text-gray-500' : 'text-gray-700'}`}>
                {item.label}
              </span>
              <StatusBadge done={item.done} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'onboarding' | 'files' | 'logs' | 'feedback' | 'sdl_agent' | 'domain_experts'

// ── Main page ────────────────────────────────────────────────────────────────

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [cacheClearing, setCacheClearing] = useState(false)

  // Load tenant from cache first, then fetch list if needed
  const { data: tenants = [] } = useQuery<TenantResponse[]>({
    queryKey: ['tenants'],
    queryFn: apiClient.getTenants,
  })

  const tenant = tenants.find((t) => t.id === id) ?? null
  const clientAdminEnabled = tenant?.client_admin_enabled ?? true
  const domainExpertEnabled = tenant?.domain_expert_enabled ?? false

  // Load event logs
  const { data: logs = [] } = useQuery<TenantLog[]>({
    queryKey: ['tenantLogs', id],
    queryFn: () => apiClient.getTenantLogs(id!),
    enabled: !!id,
  })

  // Load feedback count (same query key as TenantFeedbackTab — React Query deduplicates)
  const { data: feedbackData } = useQuery({
    queryKey: ['feedback', id],
    queryFn: () => apiClient.getFeedback({ tenant_id: id!, limit: 100 }),
    enabled: !!id && !!tenant && clientAdminEnabled,
  })
  const feedbackCount = feedbackData?.total ?? feedbackData?.items?.length ?? 0

  const clearCacheMutation = useMutation({
    mutationFn: () => apiClient.clearTenantCache(id!),
    onMutate: () => setCacheClearing(true),
    onSuccess: (data) => {
      setCacheClearing(false)
      setToast({
        message: `Cache cleared! ${data.details?.total_keys_cleared ?? 0} entries removed.`,
        type: 'success',
      })
    },
    onError: (err: Error) => {
      setCacheClearing(false)
      setToast({ message: `Failed to clear cache: ${err.message}`, type: 'error' })
    },
  })

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading tenant...</p>
      </div>
    )
  }

  // ── Checklist stats ────────────────────────────────────────────────────────

  const createChecklist = tenant.create_tenant_checklist ?? {
    tenant_created_on_lawmabase: false,
    dns_entries_added: false,
    custom_domains_added_in_azure: false,
    ssl_certificates_created_and_tested: false,
  }
  const sdlChecklist = tenant.sdl_onboarding_checklist ?? {
    crawl_mds_uploaded: false,
    marketing_audit_uploaded: false,
    competitor_crawl_uploaded: false,
    questionaire_uploaded: false,
    other_mds_uploaded: false,
    sdl_generated: false,
    sdl_uploaded_to_qm: false,
    complete_sdl_qa_pass: false,
  }
  const buildChecklist = tenant.build_client_content_checklist ?? {
    assets_added: false,
    faq_category_labels_created: false,
    ctas_setup: false,
    homepages_and_variations_setup: false,
    production_key_created: false,
    preview_key_created: false,
    knowledgebase_sync_job_added: false,
    reviews_sync_job_added: false,
    integrations_added: false,
    appointment_scheduling_configured: false,
    widget_configuration_done: false,
    contact_settings_adjusted: false,
    advanced_prompt_customized: false,
    ama_fully_tested: false,
    questions_fully_tested: false,
  }

  const createItems = Object.entries(createChecklist).map(([key, done]) => ({
    key,
    label: getLabel(createTenantLabels, key, tenant.subdomain),
    done: done as boolean,
  }))
  const sdlItems = Object.entries(sdlChecklist).map(([key, done]) => ({
    key,
    label: getLabel(sdlLabels, key, tenant.subdomain),
    done: done as boolean,
  }))
  const buildItems = Object.entries(buildChecklist).map(([key, done]) => ({
    key,
    label: getLabel(buildContentLabels, key, tenant.subdomain),
    done: done as boolean,
  }))

  const createCompleted = createItems.filter((i) => i.done).length
  const sdlCompleted = sdlItems.filter((i) => i.done).length
  const buildCompleted = buildItems.filter((i) => i.done).length

  const totalTasks = createItems.length + sdlItems.length + buildItems.length
  const completedTasks = createCompleted + sdlCompleted + buildCompleted
  const pendingTasks = totalTasks - completedTasks

  const createAllDone = createCompleted === createItems.length
  const sdlAllDone = sdlCompleted === sdlItems.length
  const buildAllDone = buildCompleted === buildItems.length

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  const tabs: { id: TabId; label: string; count?: number; hidden?: boolean }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'onboarding', label: 'Onboarding Tasks' },
    { id: 'files', label: 'SOT Files' },
    { id: 'logs', label: 'Event logs', count: logs.length },
    { id: 'feedback', label: 'Feedback', count: feedbackCount, hidden: !clientAdminEnabled },
    { id: 'sdl_agent', label: 'SDL Agent', hidden: !clientAdminEnabled },
    { id: 'domain_experts', label: 'Expert Agents', hidden: !domainExpertEnabled },
  ]
  const visibleTabs = tabs.filter((tab) => !tab.hidden)
  const currentTab: TabId = visibleTabs.some((tab) => tab.id === activeTab) ? activeTab : 'overview'

  const productLabels = [
    clientAdminEnabled && 'Client Admin',
    domainExpertEnabled && 'Domain Expert Agents',
  ].filter(Boolean) as string[]

  return (
    <div>
      {/* ── Top bar ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <button
          onClick={() => navigate('/tenants')}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors self-start"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to tenant
        </button>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => clearCacheMutation.mutate()}
            disabled={cacheClearing}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            <RefreshCwIcon className={`h-4 w-4 ${cacheClearing ? 'animate-spin' : ''}`} />
            Clear cache
          </button>
          <button
            onClick={() => setEditModalOpen(true)}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <PencilIcon className="h-4 w-4" />
            Edit tenant
          </button>
        </div>
      </div>

      {/* ── Page heading ── */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{tenant.name}</h1>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getTenantStatusBadgeClass(tenant.status)}`}
        >
          {getTenantStatusLabel(tenant.status)}
        </span>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-md transition-colors whitespace-nowrap flex-shrink-0 ${currentTab === tab.id
              ? 'border border-gray-200 border-b-white bg-white text-primary-600 -mb-px'
              : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-gray-100 text-gray-600">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Overview Tab ── */}
      {currentTab === 'overview' && (
        <div>
          <h2 className="text-base font-semibold text-gray-900 mb-4">Tenant Information</h2>
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            {[
              { label: 'Tenant name:', value: tenant.name },
              {
                label: 'Business type:',
                value: tenant.tenant_type === 'product' ? 'Product company' : 'Service company',
              },
              {
                label: 'Subdomain:',
                value: (
                  <a
                    href={getTenantUrl(tenant.subdomain)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 font-semibold"
                  >
                    {getTenantDisplayUrl(tenant.subdomain)}
                    <ExternalLinkIcon className="h-3.5 w-3.5" />
                  </a>
                ),
              },
              {
                label: 'Products:',
                value: (
                  <span className="inline-flex flex-wrap justify-end gap-1.5">
                    {productLabels.map((label) => (
                      <span
                        key={label}
                        className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700"
                      >
                        {label}
                      </span>
                    ))}
                  </span>
                ),
              },
              {
                label: 'Conversation goal:',
                value: tenant.goal || <span className="text-gray-400">Not set</span>,
              },
              { label: 'Admin', value: tenant.admin_count },
              { label: 'Created:', value: formatDate(tenant.created_at) },
              { label: 'Last activity:', value: formatDate(tenant.updated_at) },
              {
                label: 'Build date:',
                value: tenant.build_date ? (
                  formatDate(tenant.build_date)
                ) : (
                  <span className="text-gray-400">Not set</span>
                ),
              },
              {
                label: 'Status:',
                value: getTenantStatusLabel(tenant.status),
              },
            ].map((row, i) => (
              <div
                key={i}
                className="flex items-start justify-between px-6 py-4 border-b border-gray-50 last:border-0"
              >
                <span className="text-sm text-gray-500 flex-shrink-0 w-48">{row.label}</span>
                <span className="text-sm font-semibold text-gray-900 text-right">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Onboarding Tasks Tab ── */}
      {currentTab === 'onboarding' && (
        <div>
          {/* Summary stats */}
          <div className="grid grid-cols-3 divide-x divide-gray-100 bg-white border border-gray-100 rounded-xl overflow-hidden mb-6">
            {[
              { label: 'Total Tasks', value: totalTasks },
              { label: 'Completed Tasks', value: completedTasks },
              { label: 'Pending Tasks', value: pendingTasks },
            ].map((stat, i) => (
              <div key={i} className="px-8 py-6">
                <p className="text-4xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Checklists */}
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden px-4">
            <ChecklistSection
              title="Create new tenant checklist"
              items={createItems}
              completedCount={createCompleted}
              totalCount={createItems.length}
              badgeLabel={createAllDone ? 'Complete' : 'In-process'}
              badgeColor={
                createAllDone
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-50 text-yellow-700'
              }
            />
            <ChecklistSection
              title="SDL onboarding checklist"
              items={sdlItems}
              completedCount={sdlCompleted}
              totalCount={sdlItems.length}
              badgeLabel={sdlAllDone ? 'Complete: Ready for LARS' : 'In-process'}
              badgeColor={
                sdlAllDone
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-50 text-yellow-700'
              }
            />
            <ChecklistSection
              title="Build client content checklist"
              items={buildItems}
              completedCount={buildCompleted}
              totalCount={buildItems.length}
              badgeLabel={buildAllDone ? 'Complete' : 'In- process'}
              badgeColor={
                buildAllDone
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-50 text-yellow-700'
              }
            />
          </div>
        </div>
      )}

      {/* ── SOT Files Tab ── */}
      {currentTab === 'files' && (
        <SOTFileBrowser subdomain={tenant.subdomain} />
      )}

      {/* ── Event Logs Tab ── */}
      {currentTab === 'logs' && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          {logs.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-400">No event logs found</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {logs.map((log, i) => (
                <div key={i} className="px-6 py-4 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800">{log.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(log.created_at)}</p>
                  </div>
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                    {log.current_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Feedback Tab ── */}
      {currentTab === 'feedback' && <TenantFeedbackTab tenantId={id!} />}

      {/* ── SDL Agent Tab ── */}
      {currentTab === 'sdl_agent' && <SDLAgentTab tenantId={id!} subdomain={tenant.subdomain} />}

      {/* ── Expert Agents Tab ── */}
      {currentTab === 'domain_experts' && <TenantDomainExpertsTab tenantId={id!} />}

      {/* ── Modals & Toast ── */}
      <EditTenantModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false)
          queryClient.invalidateQueries({ queryKey: ['tenants'] })
        }}
        tenant={tenant}
        onSuccess={() => setToast({ message: 'Tenant updated successfully', type: 'success' })}
        onError={(msg) => setToast({ message: msg, type: 'error' })}
      />

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  )
}
